import { HStack, PageContainer, Text, VStack } from '@/components/Atoms'
import { Block } from '@/components/Block'
import { CrtImportHistoryButtons } from '@/components/CrtImportHistoryButtons'
import { CrtImportPeriodNotFound } from '@/components/CrtImportPeriodNotFound'
import { DetailsHeader } from '@/components/DetailsHeader'
import { ExportCsvMenu, ExportCsvMenuItem } from '@/components/ExportCsv/ExportCsvMenu'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import { Spinner } from '@/components/Spinner'
import {
  calculationRunAccrualByPeriodQueryOptions,
  calculationRunAccrualDetailsExportQueryOptions,
  calculationRunAccrualDetailsRawExportQueryOptions,
  calculationRunAccrualsDetailsSearchSchema,
  useCalculationRunAccrualById,
  useUpdateCalculationRunAccrual,
} from '@/hooks/queries/crt/useCalculationRunAccruals'
import { useLastUpdatedCalculationRunAccrualsLog } from '@/hooks/queries/useAuditTrails'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { useTablePagination } from '@/hooks/useTablePagination'
import { formatLastUpdatedAssumptionLog } from '@/lib/utils/crt'
import { crtPeriodFromParams } from '@/lib/utils/crtPeriodParams'
import { queryClient } from '@/main'
import { parseDateOnly } from '@/utils/DateOnlyHelpers'
import { UpdateCalculationRunAccrual } from '@repo/backend/crt'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CalculationRunAccrualDetailsDataTable from './-components/CalculationRunAccrualDetails/CalculationRunAccrualDetailsDataTable'

export const Route = createFileRoute(
  '/_auth/_modules/crt/customer-investments/insights/calculation-run-accruals/$year/$month',
)({
  component: CalculationRunAccrualDetailsPeriod,
  validateSearch: (search) => calculationRunAccrualsDetailsSearchSchema.parse(search),
  loader: async ({ params }) => {
    // The URL names the import's period — its month of billing — not a CRT period.
    const period = crtPeriodFromParams(params)
    const calculationRunAccrual = period
      ? await queryClient.fetchQuery(calculationRunAccrualByPeriodQueryOptions(period))
      : null

    return {
      crumb: calculationRunAccrual?.name ?? `${params.year}/${params.month}`,
      period,
      calculationRunAccrualId: calculationRunAccrual ? String(calculationRunAccrual.id) : null,
    }
  },
})

function CalculationRunAccrualDetailsPeriod() {
  const { formatMonthYear } = useLocalizedDate()
  const { year, month } = Route.useParams()
  const { period, calculationRunAccrualId } = Route.useLoaderData()

  if (!calculationRunAccrualId) {
    return (
      <CrtImportPeriodNotFound
        period={period ? formatMonthYear(parseDateOnly(period)) : `${year}/${month}`}
        otherPeriodsTo="/crt/customer-investments/insights/calculation-run-accruals"
      />
    )
  }

  return (
    <CalculationRunAccrualDetails
      key={calculationRunAccrualId}
      calculationRunAccrualId={calculationRunAccrualId}
    />
  )
}

function CalculationRunAccrualDetails({
  calculationRunAccrualId,
}: {
  calculationRunAccrualId: string
}) {
  const { t } = useTranslation(['crt', 'common'])
  const { formatMonthYear } = useLocalizedDate()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const { data, isPending, isError } = useCalculationRunAccrualById(calculationRunAccrualId, {
    ...search,
    page: search.page ?? 1,
    pageSize,
  })
  const { data: lastUpdatedTrail } =
    useLastUpdatedCalculationRunAccrualsLog(calculationRunAccrualId)

  const [formState, setFormState] = useState({
    name: '',
    description: '',
  })

  useEffect(() => {
    if (data) {
      setFormState({
        name: data.name,
        description: data.description ?? '',
      })
    }
  }, [data])

  const getUnsavedChanges = (): UpdateCalculationRunAccrual | null => {
    if (!data) return null

    const changes: UpdateCalculationRunAccrual = {}
    let hasChanges = false

    if (formState.name !== data.name) {
      changes.name = formState.name
      hasChanges = true
    }

    if (formState.description !== (data.description ?? '')) {
      changes.description = formState.description
      hasChanges = true
    }

    return hasChanges ? changes : null
  }

  const hasUnsavedChanges = getUnsavedChanges() !== null

  const lastUpdatedLog = formatLastUpdatedAssumptionLog(lastUpdatedTrail)

  const { mutate: updateCalculationRunAccrual, isPending: isUpdatePending } =
    useUpdateCalculationRunAccrual({
      calculationRunAccrualId: Number(calculationRunAccrualId),
    })

  const handleUpdateName = (name: string) => {
    setFormState((prev) => ({ ...prev, name }))
  }

  const handleUpdateDescription = (description: string) => {
    setFormState((prev) => ({ ...prev, description }))
  }

  const handleSave = () => {
    const unsavedChanges = getUnsavedChanges()
    if (!unsavedChanges) return

    updateCalculationRunAccrual(unsavedChanges)
  }

  if (isPending) {
    return (
      <PageContainer className="flex items-center justify-center">
        <Spinner size="lg" />
      </PageContainer>
    )
  }

  if (isError || !data) {
    return (
      <PageContainer maxWidth="wide">
        <Text>{t('errors.calculationRunAccruals.fetchFailed')}</Text>
      </PageContainer>
    )
  }

  return (
    <PageContainer className="pb-28" maxWidth="wide">
      <Block shouldBlock={hasUnsavedChanges} />

      <HStack gap="sm" className="justify-between">
        <DetailsHeader
          name={formState.name}
          description={formState.description}
          onUpdateName={handleUpdateName}
          onUpdateDescription={handleUpdateDescription}
          permission="crt_calculation_run_accruals_edit"
        />

        <VStack gap="md" className="items-end">
          <Text className="text-muted-foreground text-sm">
            {formatMonthYear(parseDateOnly(data.period))}
          </Text>
          <HStack gap="sm">
            <ExportCsvMenu
              items={[
                <ExportCsvMenuItem
                  label={t('export.viewData', { ns: 'common' })}
                  description={t('export.viewDataDescription', { ns: 'common' })}
                  getCsvExport={() =>
                    queryClient.ensureQueryData(
                      calculationRunAccrualDetailsExportQueryOptions(
                        calculationRunAccrualId,
                        search,
                      ),
                    )
                  }
                  rowCount={data.detailsRowCount}
                  canExceedRowCountLimit
                />,
                <ExportCsvMenuItem
                  label={t('export.rawData', { ns: 'common' })}
                  description={t('export.rawDataDescription', { ns: 'common' })}
                  getCsvExport={() =>
                    queryClient.ensureQueryData(
                      calculationRunAccrualDetailsRawExportQueryOptions(calculationRunAccrualId),
                    )
                  }
                  rowCount={data.detailsRawRowCount}
                  canExceedRowCountLimit
                />,
              ]}
            />
            <CrtImportHistoryButtons
              otherPeriodsTo="/crt/customer-investments/insights/calculation-run-accruals"
              uploadType="import-calculation-run-accruals-details"
              uploadAttributes={{ 'calculation-run-accrual-id': calculationRunAccrualId }}
            />
          </HStack>
        </VStack>
      </HStack>

      <CalculationRunAccrualDetailsDataTable
        calculationRunAccrualId={calculationRunAccrualId}
        calculationRunAccrual={data}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />

      <PermissionsGuard required={['crt_calculation_run_accruals_edit']}>
        <PendingChanges
          areChangesPending={hasUnsavedChanges}
          isSavePending={isUpdatePending}
          lastUpdatedLog={lastUpdatedLog}
          onSaveChanges={handleSave}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
